import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { Smartphone, Apple, Globe, Download, QrCode, Clock, Sparkles } from 'lucide-react';
import { useSiteConfig, type AppDownloadPlatform } from '../../hooks/useSiteConfig';
import { SiteFiling } from '../../components/common/SiteFiling';

/**
 * App 下载页（公开，免登录——新用户装 App 前还没有账号）。
 *
 * 三平台读数全部来自后台「站点信息 → App 下载页」，经 GET /public/config 下发、本页实时读取：
 * 后台改完即生效，不必重新构建前端。三端各自按链接现场生成二维码（Android 扫直链下 APK，iOS / 鸿蒙扫商店链接直达商店页）；
 * 未上架的平台整卡置灰走「敬请期待」，避免出现「按钮能点、链接是空」的死路。
 */
export default function DownloadPage() {
  const { siteName, logoUrl, appDownloads } = useSiteConfig();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* 顶部导航（与 /about 同一套外壳） */}
      <nav className={`fixed top-0 left-0 right-0 z-50 h-[70px] transition-all duration-500 ${isScrolled ? 'bg-white/95 backdrop-blur-md border-b border-slate-100' : 'bg-transparent'}`}>
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 h-full flex items-center justify-between">
          <a href="/login" className="flex items-center gap-3">
            <img src={logoUrl} alt="Logo" className="w-10 h-10" />
            <span className={`text-lg font-bold tracking-wide transition-colors ${isScrolled ? 'text-slate-800' : 'text-white'}`}>
              {siteName}
            </span>
          </a>
          <div className="flex items-center gap-6">
            <a href="/help" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              帮助中心
            </a>
            <a href="/download" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-teal-600 hover:opacity-70' : 'text-white'}`}>
              下载 App
            </a>
            <a href="/about" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-slate-600 hover:opacity-70' : 'text-white/90 hover:text-white'}`}>
              关于我们
            </a>
            <a
              href="/login"
              className={`text-sm font-medium px-5 py-2 rounded-full transition-all ${isScrolled ? 'bg-teal-600 text-white hover:bg-teal-700' : 'border border-white/70 text-white hover:bg-white/20'}`}
            >
              立即登录
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-[140px] pb-16 bg-gradient-to-br from-slate-800 via-slate-700 to-teal-800">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">下载 {siteName}</h1>
          <p className="max-w-xl mx-auto text-sm md:text-base text-slate-300 leading-relaxed">
            选择你的设备平台，扫码或点击下载。数据与网页端实时同步，随时随地记录每一笔订单与预约。
          </p>
        </div>
      </section>

      {/* 平台卡片 */}
      <main className="flex-1 py-14">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          {/* 面包屑 */}
          <div className="text-xs text-slate-500 mb-8">
            <a href="/login" className="hover:text-teal-600 transition-colors">首页</a>
            <span className="mx-2">/</span>
            <span className="text-slate-700 font-semibold">下载 App</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {/* 三端统一：已上架即按各自链接现场生成二维码，扫码直达（Android 下 APK，iOS/鸿蒙进商店页） */}
            <PlatformCard
              name="Android"
              icon={<Smartphone className="h-6 w-6" />}
              accent="android"
              platform={appDownloads.android}
              scanHint="手机扫码直接下载安装"
            />

            <PlatformCard
              name="iOS"
              icon={<Apple className="h-6 w-6" />}
              accent="ios"
              platform={appDownloads.ios}
              scanHint="手机扫码打开 App Store"
            />

            <PlatformCard
              name="鸿蒙 HarmonyOS"
              icon={<Globe className="h-6 w-6" />}
              accent="harmony"
              platform={appDownloads.harmony}
              scanHint="手机扫码打开华为应用市场"
            />
          </div>

          {/* 版本读数（仅当有任一已上架平台时展示，避免整屏空荡） */}
          {(appDownloads.android.enabled || appDownloads.ios.enabled || appDownloads.harmony.enabled) && (
            <div className="mt-10 flex items-start gap-2 text-xs text-slate-500 leading-relaxed">
              <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-teal-600" />
              <p>
                安装包与版本信息以本页实时内容为准。若你的设备暂时不在支持列表内，可先使用网页端，功能完全一致。
              </p>
            </div>
          )}
        </div>
      </main>

      {/* 底部导航（与 /about 同一套） */}
      <footer className="bg-slate-800 text-white py-8 mt-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img src={logoUrl} alt="Logo" className="h-8 w-auto" />
              <div>
                <div className="font-semibold text-lg">{siteName}</div>
                <div className="text-xs text-slate-400">让每一笔业务都有据可查</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-6 text-sm text-slate-400">
              <a href="/terms" className="hover:text-white transition-colors">服务条款</a>
              <a href="/privacy" className="hover:text-white transition-colors">隐私政策</a>
              <a href="/help" className="hover:text-white transition-colors">帮助中心</a>
              <a href="/download" className="text-white font-medium transition-colors">下载 App</a>
              <a href="/contact" className="hover:text-white transition-colors">联系我们</a>
              <a href="/about" className="hover:text-white transition-colors">关于我们</a>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-8 pt-6 text-center text-xs text-slate-500">
            <SiteFiling />
          </div>
        </div>
      </footer>
    </div>
  );
}

type CardAccent = 'android' | 'ios' | 'harmony';

const ACCENT: Record<CardAccent, { chip: string; button: string }> = {
  // 强调色统一用 teal（与站点主色、/about 一致），三卡靠图标与平台名区分，不各配一套花哨品牌色。
  android: { chip: 'bg-emerald-50 text-emerald-600', button: 'bg-teal-600 hover:bg-teal-700' },
  ios: { chip: 'bg-slate-100 text-slate-700', button: 'bg-teal-600 hover:bg-teal-700' },
  harmony: { chip: 'bg-sky-50 text-sky-600', button: 'bg-teal-600 hover:bg-teal-700' },
};

/** 由链接现场生成二维码 dataURL；链接为空或生成失败时返回空串（此时不显示二维码，下载按钮仍在）。 */
function useQrDataUrl(url: string): string {
  const [dataUrl, setDataUrl] = useState('');
  useEffect(() => {
    if (!url) {
      setDataUrl('');
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(url, { width: 220, margin: 2, color: { dark: '#0f172a', light: '#ffffff' } })
      .then((value) => {
        if (!cancelled) setDataUrl(value);
      })
      .catch(() => {
        if (!cancelled) setDataUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, [url]);
  return dataUrl;
}

function PlatformCard({
  name,
  icon,
  accent,
  platform,
  scanHint,
}: {
  name: string;
  icon: React.ReactNode;
  accent: CardAccent;
  platform: AppDownloadPlatform;
  scanHint: string;
}) {
  const available = platform.enabled && platform.url.trim() !== '';
  const style = ACCENT[accent];
  // 仅已上架才出码：未上架的占位链接没有扫码意义。三端统一按各自链接生成。
  const qr = useQrDataUrl(available ? platform.url.trim() : '');

  return (
    <div
      className={`flex flex-col rounded-2xl border p-6 transition-shadow ${
        available
          ? 'bg-white border-slate-100 shadow-sm hover:shadow-md'
          : 'bg-slate-50 border-slate-100'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${available ? style.chip : 'bg-slate-100 text-slate-400'}`}>
          {icon}
        </div>
        <div>
          <div className={`text-lg font-semibold ${available ? 'text-slate-800' : 'text-slate-400'}`}>{name}</div>
          {platform.version.trim() && (
            <div className="text-xs text-slate-500">版本 {platform.version.trim()}</div>
          )}
        </div>
      </div>

      {/* 已上架：读数 + 下载按钮 + 扫码二维码 */}
      {available ? (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 min-h-[1rem]">
            {platform.size?.trim() && <span>{platform.size.trim()}</span>}
            {platform.updatedAt?.trim() && (
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {platform.updatedAt.trim()}
              </span>
            )}
          </div>
          {platform.changelog?.trim() && (
            <p className="mt-3 text-xs text-slate-500 leading-relaxed whitespace-pre-line line-clamp-4">
              {platform.changelog.trim()}
            </p>
          )}
          <a
            href={platform.url}
            target="_blank"
            rel="noopener noreferrer"
            download={accent === 'android' ? true : undefined}
            className={`mt-4 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-semibold transition-colors ${style.button}`}
          >
            <Download className="h-4 w-4" />
            立即下载
          </a>
          {qr && (
            <div className="mt-4 flex flex-col items-center">
              <div className="p-3 bg-white rounded-xl border border-slate-100 shadow-sm">
                <img src={qr} alt={`${name} 下载二维码`} className="w-40 h-40" />
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                <QrCode className="h-3.5 w-3.5" />
                {scanHint}
              </div>
            </div>
          )}
        </>
      ) : (
        /* 未上架：置灰 + 敬请期待，不给可点的死按钮 */
        <>
          <p className="mt-4 text-sm text-slate-400 leading-relaxed flex-1">
            该版本正在准备中，敬请期待。你可以先使用网页端，功能完全一致。
          </p>
          <div className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-slate-100 text-slate-400 text-sm font-semibold cursor-not-allowed select-none">
            敬请期待
          </div>
        </>
      )}
    </div>
  );
}
